var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { getApplicativeMonoid } from 'fp-ts/Applicative';
import { bind as bind_, chainFirst as chainFirst_ } from 'fp-ts/Chain';
import { chainFirstIOK as chainFirstIOK_, chainIOK as chainIOK_ } from 'fp-ts/FromIO';
import { chainFirstReaderK as chainFirstReaderK_, chainReaderK as chainReaderK_ } from 'fp-ts/FromReader';
import { chainFirstTaskK as chainFirstTaskK_, chainTaskK as chainTaskK_ } from 'fp-ts/FromTask';
import { flow, identity, pipe, SK } from 'fp-ts/function';
import * as R from 'fp-ts/Reader';
import { flap, Merge } from '../Observable';
import { Applicative, Apply, FromIO, fromIO, fromObservable, fromObservableK, fromOption, FromReader, fromReaderTask, fromReaderTaskK, FromTask, fromTask, fromTaskOptionK } from './ReaderObservable';
// -------------------------------------------------------------------------------------
// non-pipeables
// -------------------------------------------------------------------------------------
var _chain = function (ma, f) { return pipe(ma, chain(f)); };
// -------------------------------------------------------------------------------------
// combinators
// -------------------------------------------------------------------------------------
export var chainOptionK = 
/*#__PURE__*/
function (f) { return chain(function (a) { return fromOption(f(a)); }); };
// -------------------------------------------------------------------------------------
// type class members
// -------------------------------------------------------------------------------------
/**
 * Composes computations in sequence, using the return value of one computation to determine the next computation.
 *
 * @category Monad
 */
export var chain = function (f) { return function (ma) { return function (r) {
    return pipe(ma(r), Merge.chain(function (a) { return f(a)(r); }));
}; }; };
/**
 * Less strict version of  [`chain`](#chain).
 *
 * @category Monad
 */
export var chainW = chain;
/**
 * Derivable from `Monad`.
 */
export var flatten = 
/*#__PURE__*/
function (concurrency) {
    if (concurrency === void 0) { concurrency = Infinity; }
    return function (ma) {
        return function (r) {
            return pipe(ma(r), flap(r), Merge.flatten(concurrency));
        };
    };
};
/**
 * Less strict version of [`flatten`](#flatten).
 *
 * @category combinators
 */
export var flattenW = flatten;
// -------------------------------------------------------------------------------------
// instances
// -------------------------------------------------------------------------------------
/**
 * @category instances
 */
export var getMonoid = function () {
    return getApplicativeMonoid(R.Applicative)(Merge.getMonoid());
};
/**
 * @category instances
 */
export var Chain = __assign(__assign({}, Apply), { chain: _chain });
/**
 * Composes computations in sequence, using the return value of one computation to determine the next computation and
 * keeping only the result of the first.
 *
 * Derivable from `Monad`.
 */
export var chainFirst = chainFirst_(Chain);
/**
 * Less strict version of [`chainFirst`](#chainfirst).
 *
 * Derivable from `Chain`.
 *
 * @category combinators
 */
export var chainFirstW = chainFirst;
/**
 * @category combinators
 */
export var chainIOK = 
/*#__PURE__*/
chainIOK_(FromIO, Chain);
/**
 * @category combinators
 */
export var chainFirstIOK = 
/*#__PURE__*/
chainFirstIOK_(FromIO, Chain);
/**
 * @category combinators
 */
export var chainTaskK = 
/*#__PURE__*/
chainTaskK_(FromTask, Chain);
/**
 * @category combinators
 */
export var chainFirstTaskK = 
/*#__PURE__*/
chainFirstTaskK_(FromTask, Chain);
/**
 * @category combinators
 */
export var chainTaskOptionK = flow(fromTaskOptionK, chainW);
/**
 * @category combinators
 */
export var chainReaderK = 
/*#__PURE__*/
chainReaderK_(FromReader, Chain);
/**
 * Less strict version of [`chainReaderK`](#chainreaderk).
 *
 * @category combinators
 */
export var chainReaderKW = chainReaderK;
/**
 * @category combinators
 */
export var chainFirstReaderK = 
/*#__PURE__*/
chainFirstReaderK_(FromReader, Chain);
/**
 * Less strict version of [`chainFirstReaderK`](#chainfirstreaderk).
 *
 * @category combinators
 */
export var chainFirstReaderKW = chainFirstReaderK;
/**
 * @category instances
 */
export var Monad = __assign(__assign({}, Applicative), { chain: _chain });
/**
 * @category instances
 */
export var MonadIO = __assign(__assign({}, Applicative), { chain: _chain, fromIO: fromIO });
/**
 * @category instances
 */
export var MonadTask = __assign(__assign({}, Applicative), { chain: _chain, fromIO: fromIO, fromTask: fromTask });
/**
 * @category combinators
 */
export var chainReaderTaskKW = function (f) { return chainW(function (a) { return fromReaderTask(f(a)); }); };
/**
 * @category combinators
 */
export var chainReaderTaskK = chainReaderTaskKW;
/**
 * @category combinators
 */
export var chainFirstReaderTaskKW = function (f) { return chainFirstW(fromReaderTaskK(f)); };
/**
 * @category combinators
 */
export var chainFirstReaderTaskK = chainFirstReaderTaskKW;
export var MonadObservable = __assign(__assign({}, Applicative), { chain: _chain, fromIO: fromIO, fromTask: fromTask, fromObservable: fromObservable });
/**
 * @category combinators
 */
export var chainObservableK = function (f) { return chainW(function (a) { return fromObservable(f(a)); }); };
/**
 * @category combinators
 */
export var chainFirstObservableK = function (f) { return chainFirstW(fromObservableK(f)); };
// -------------------------------------------------------------------------------------
// array utils
// -------------------------------------------------------------------------------------
/**
 * Equivalent to `ReadonlyNonEmptyArray#traverseWithIndex(Applicative)`.
 */
export var traverseReadonlyArrayWithIndex = function (concurrency) {
    return function (f) {
        return flow(R.traverseReadonlyArrayWithIndex(f), R.map(Merge.traverseReadonlyArrayWithIndex(concurrency)(SK)));
    };
};
export var traverseReadonlyArray = function (concurrency) {
    return function (f) {
        return traverseReadonlyArrayWithIndex(concurrency)(flow(SK, f));
    };
};
export var sequenceReadonlyArray = 
/*#__PURE__*/
function (concurrency) { return traverseReadonlyArray(concurrency)(identity); };
// -------------------------------------------------------------------------------------
// do notation
// -------------------------------------------------------------------------------------
export var bind = bind_(Chain);
